import { useQuery } from '@tanstack/react-query';

import {
  getSchedulerType,
  InterviewType,
  useGetNylasConnectionStatusQuery,
} from '@/features/nylas-calendar-manage-connection-modal';
import { isCalendarValid, useNylasCalendarInterviewType } from '@/features/nylas-calendar-manage-connection-modal/lib';
import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { CalendarSchedulingLinkResponse } from 'clients/NotificationsClient/NotificationsClient.types';
import { QueryKey } from 'types/query';

export const useCalendarSchedulingLinkQuery = (context?: {
  esPersonId?: string;
  campaignId?: string | number;
  interviewType?: InterviewType | null;
  jobTitle?: string;
  jobImprovedId?: number;
  autoFillJobContext?: boolean;
}) => {
  const { data: nylasConnectionStatus } = useGetNylasConnectionStatusQuery();
  const isCalendarStatusValid = isCalendarValid(nylasConnectionStatus);
  const interviewType = useNylasCalendarInterviewType();

  const isCampaignIdValid = typeof context?.campaignId === 'string' || typeof context?.campaignId === 'number';
  const isCandidateValid = typeof context?.esPersonId === 'string';
  const isRjCommHubContext = context && isCampaignIdValid && isCandidateValid;

  return useQuery<CalendarSchedulingLinkResponse>(
    [
      QueryKey.useSchedulingLinkQuery,
      context?.campaignId,
      context?.esPersonId,
      context?.interviewType ?? interviewType,
      context?.jobTitle,
      context?.jobImprovedId,
      context?.autoFillJobContext,
    ],
    async () => {
      if (context && (!isCampaignIdValid || !isCandidateValid)) {
        throw new Error('Campaign ID and esPersonId are required');
      }

      const payloadContext =
        context?.campaignId && context.esPersonId
          ? {
              campaign_id: context.campaignId.toString(),
              es_person_id: context.esPersonId,
              ...(!!context.jobTitle && { job_title: context.jobTitle }),
              ...(!!context.jobImprovedId && { job_improved_id: context.jobImprovedId }),
            }
          : undefined;

      const options =
        context?.autoFillJobContext !== undefined ? { autoFillJobContext: context.autoFillJobContext } : undefined;

      const { data } = await notificationsClient.getCalendarSchedulingLink(
        payloadContext,
        getSchedulerType(context?.interviewType ?? interviewType),
        options,
      );

      return data;
    },
    {
      retry: false,
      enabled: isRjCommHubContext ? isCalendarStatusValid : true,
    },
  );
};
